"use client"

import "@fontsource/dm-sans"
import "@fontsource/dm-mono"
import "mapbox-gl/dist/mapbox-gl.css"

import {
  useCallback,
  useEffect,
  useState,
  useSyncExternalStore,
} from "react"
import { usePathname, useSearchParams } from "next/navigation"
import { createClient } from "@/lib/supabase/client"
import useWorldStore from "./store/worldStore"
import AgentPanel from "./components/AgentPanel"
import MasterChat from "./components/MasterChat"
import ChatWidget from "./components/ChatWidget"
import ChatEmbedWindow from "./components/ChatEmbedWindow"
import CallWindow from "./components/CallWindow"
import DroppedAgentTaskComposer from "./components/DroppedAgentTaskComposer"
import TopBar from "./components/TopBar"
import ActivityFeed from "./components/ActivityFeed"
import TaskManager from "./components/TaskManager"
import TaskResultModal from "./components/TaskResultModal"
import TaskToasts from "./components/TaskToast"
import { ToastContainer } from "@/components/ui/toast"
import { useToastStore } from "@/lib/toast-store"
import AgentCreator from "./components/AgentCreator"
import { LoginScreen } from "@/components/auth/LoginScreen"
import { OnboardingScreen } from "@/components/auth/OnboardingScreen"
import EgoWorldClient from "./EgoWorldClient"
import useScheduleGen from "./simulation/useScheduleGen"
import {
  fetchWorldBootstrap,
  importWorldAgents,
  streamWorldTaskEvents,
} from "../../lib/backend/world"
import { getSceneKey, regenerateScene } from "./services/sceneService"
import { CHAT_APP_WORLD_TASKS_CHANGED_EVENT } from "./lib/chatAppEmbed"
import {
  readCachedBootstrap,
  writeCachedBootstrap,
  clearCachedBootstrap,
} from "./lib/bootstrapCache"
import {
  beginColdBootstrapToast,
  updateColdBootstrapToast,
  finishColdBootstrapToast,
  dismissColdBootstrapToast,
  errorColdBootstrapToast,
  beginRevalidateToast,
} from "./lib/bootstrapToasts"
import { getTaskIdentity } from "./lib/taskIdentity"
import { needsOpenClawOnboarding } from "@/lib/openclawOnboarding.js"

import "./index.css"

const AUTH_PATHS = ["/onboarding", "/auth"]
const AUTH_GATE_TIMEOUT_MS = 5000
const BOOTSTRAP_RETRY_DELAY_MS = 750
const BOOTSTRAP_MAX_RETRIES = 8
const WORLD_IMPORT_INITIAL_WAIT_MS = 2000
const DEV_GUEST_BYPASS_KEY = "ego-world-dev-guest-bypass"
const WORLD_DEBUG_ENABLED =
  process.env.NODE_ENV !== "production" ||
  ["1", "true", "yes", "on"].includes(
    String(process.env.NEXT_PUBLIC_EGO_WORLD_DEBUG || "")
      .trim()
      .toLowerCase()
  )
function getBootstrapAgentIdSignature(bootstrap) {
  return (bootstrap?.agents || [])
    .map((agent) => agent?.openclaw_agent_id || "")
    .filter(Boolean)
    .join("|")
}

function getStoreAgentIdSignature(agents) {
  return (agents || [])
    .map((agent) => agent?.id || "")
    .filter(Boolean)
    .join("|")
}

function isTransientBootstrapError(error) {
  const message = error instanceof Error ? error.message : String(error || "")
  return (
    message.includes("Missing character-platform session") ||
    message.includes("401") ||
    message.includes("403")
  )
}

async function fetchAuthGateState() {
  const controller = new AbortController()
  const timeoutId = window.setTimeout(
    () => controller.abort(),
    AUTH_GATE_TIMEOUT_MS
  )

  let response
  try {
    response = await fetch("/api/auth/me", {
      method: "GET",
      cache: "no-store",
      signal: controller.signal,
    })
  } catch (error) {
    if (error instanceof Error && error.name === "AbortError") {
      return { mode: "unknown" }
    }
    throw error
  } finally {
    window.clearTimeout(timeoutId)
  }

  if (response.status === 401 || response.status === 403) {
    return { mode: "login" }
  }

  if (response.ok) {
    let payload = null
    try {
      payload = await response.json()
    } catch {
      payload = null
    }

    if (payload?.openclaw_connected === false) {
      if (needsOpenClawOnboarding(payload)) {
        return { mode: "onboarding" }
      }
      return { mode: "ready" }
    }

    if (needsOpenClawOnboarding(payload)) {
      return { mode: "onboarding" }
    }

    return { mode: "ready" }
  }

  return { mode: "unknown" }
}

function DevGuestBypassPanel({ enabled, onEnable, onDisable }) {
  if (!WORLD_DEBUG_ENABLED) return null

  return (
    <div
      style={{
        position: "fixed",
        top: 84,
        left: 12,
        zIndex: 49,
        width: 240,
        borderRadius: 8,
        background: "rgba(8,10,16,0.88)",
        backdropFilter: "blur(20px)",
        border: "1px solid rgba(255,255,255,0.12)",
        padding: 12,
        display: "flex",
        flexDirection: "column",
        gap: 10,
        boxShadow: "inset 0px 1px 1px rgba(255,255,255,0.06)",
      }}
    >
      <div
        style={{
          fontFamily: "'Inter', ui-sans-serif, system-ui, sans-serif",
          fontSize: 10,
          letterSpacing: "0.12em",
          textTransform: "uppercase",
          color: "rgba(255,255,255,0.45)",
        }}
      >
        Dev Debug
      </div>
      <div
        style={{
          fontFamily: "'Inter', ui-sans-serif, system-ui, sans-serif",
          fontSize: 11,
          lineHeight: 1.5,
          color: "rgba(255,255,255,0.72)",
        }}
      >
        {enabled
          ? "Guest dummy agents are active for this tab."
          : "Skip auth and gateway setup for this tab, then load the guest dummy agents."}
      </div>
      <button
        type="button"
        onClick={enabled ? onDisable : onEnable}
        style={{
          height: 30,
          borderRadius: 8,
          background: enabled
            ? "rgba(255,120,120,0.18)"
            : "rgba(0,255,200,0.18)",
          border: `1px solid ${enabled ? "rgba(255,120,120,0.35)" : "rgba(0,255,200,0.35)"}`,
          padding: "0 12px",
          fontFamily: "'Inter', ui-sans-serif, system-ui, sans-serif",
          fontSize: 10,
          fontWeight: 500,
          color: enabled ? "#ffb4b4" : "#9fffe8",
          cursor: "pointer",
        }}
      >
        {enabled ? "Return To Standard Logic" : "Use Guest Dummy Data"}
      </button>
    </div>
  )
}

function RootShellFailureState({ message }) {
  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "72px 24px 24px",
        position: "relative",
      }}
    >
      <div
        style={{
          width: "min(420px, 100%)",
          borderRadius: 8,
          background: "rgba(8,10,16,0.88)",
          backdropFilter: "blur(20px)",
          border: "1px solid rgba(255,120,120,0.24)",
          boxShadow: "inset 0px 1px 1px rgba(255,255,255,0.06)",
          padding: 20,
          display: "flex",
          flexDirection: "column",
          gap: 12,
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            color: "rgba(255,235,235,0.92)",
            fontFamily: "'Inter', ui-sans-serif, system-ui, sans-serif",
            fontSize: 14,
            fontWeight: 500,
          }}
        >
          <span style={{ color: "#f87171", fontSize: 16 }}>!</span>
          Workspace unavailable
        </div>
        <div
          style={{
            fontFamily: "'Inter', ui-sans-serif, system-ui, sans-serif",
            fontSize: 12,
            lineHeight: 1.5,
            color: "rgba(255,255,255,0.68)",
          }}
        >
          {message || "The workspace backend failed to initialize."}
        </div>
        <div
          style={{
            fontFamily: "'Inter', ui-sans-serif, system-ui, sans-serif",
            fontSize: 11,
            lineHeight: 1.5,
            color: "rgba(255,255,255,0.42)",
          }}
        >
          Bring the backend back up, then refresh this page.
        </div>
      </div>
    </div>
  )
}

function PersistentShellContent({ children, pathname }) {
  const searchParams = useSearchParams()
  const authError = searchParams?.get("error") ?? null
  const authReason = searchParams?.get("reason") ?? null
  const isWorldEntryRoute =
    pathname === "/" || pathname === "/world" || pathname?.startsWith("/world/")
  const shouldRunWorldLiveSync = isWorldEntryRoute
  const [worldEntryAccessState, setWorldEntryAccessState] = useState(
    isWorldEntryRoute ? "checking" : "ready"
  )
  const [worldEntryErrorMessage, setWorldEntryErrorMessage] = useState("")
  const [guestBypassEnabled, setGuestBypassEnabled] = useState(false)

  useScheduleGen()
  const agents = useWorldStore((s) => s.agents)
  const seedAgents = useWorldStore((s) => s.seedAgents)
  const applyBootstrap = useWorldStore((s) => s.applyBootstrap)
  const applyAgentTaskSnapshot = useWorldStore((s) => s.applyAgentTaskSnapshot)
  const setAccessToken = useWorldStore((s) => s.setAccessToken)
  const setOpenclawConnected = useWorldStore((s) => s.setOpenclawConnected)
  const openResultModals = useWorldStore((s) => s.openResultModals)
  const closeResultModal = useWorldStore((s) => s.closeResultModal)
  const showCreator = useWorldStore((s) => s.showCreator)
  const setShowCreator = useWorldStore((s) => s.setShowCreator)
  const activeView = useWorldStore((s) => s.activeView)
  const sceneDebugGeometry = useWorldStore((s) => s.sceneDebugGeometry)
  const setSceneDebugGeometry = useWorldStore((s) => s.setSceneDebugGeometry)
  const mapZoomTarget = useWorldStore((s) => s.mapZoomTarget)
  const locationScenes = useWorldStore((s) => s.locationScenes)
  const [serverBanner, setServerBanner] = useState(false)
  const [bootstrapRetryCount, setBootstrapRetryCount] = useState(0)

  const applyResolvedBootstrap = useCallback(
    (bootstrap, options = {}) => {
      const onboardingRequired = needsOpenClawOnboarding(bootstrap?.user)
      const bootstrapOptions = options.preserveRuntime
        ? { preserveRuntime: true }
        : {}

      if (isWorldEntryRoute && onboardingRequired) {
        setOpenclawConnected(false)
        setWorldEntryErrorMessage("")
        setBootstrapRetryCount(0)
        setWorldEntryAccessState("onboarding")
        dismissColdBootstrapToast()
        clearCachedBootstrap()
        return false
      }

      if (!bootstrap?.openclaw_connected) {
        setOpenclawConnected(false)
        if (bootstrap) {
          applyBootstrap(bootstrap, bootstrapOptions)
        } else {
          seedAgents()
        }
      } else {
        setOpenclawConnected(true)
        applyBootstrap(bootstrap, bootstrapOptions)
        if (options.userId) {
          writeCachedBootstrap(options.userId, bootstrap)
        }
      }

      setWorldEntryErrorMessage("")
      setBootstrapRetryCount(0)
      setWorldEntryAccessState("ready")
      return true
    },
    [applyBootstrap, isWorldEntryRoute, seedAgents, setOpenclawConnected]
  )

  const zoomedAgent = mapZoomTarget
    ? agents.find((agent) => agent.id === mapZoomTarget) || null
    : null
  const zoomedActivityType = zoomedAgent?.activity || "working"
  const zoomedPlaceKey = (zoomedAgent?.locationTag || "home_idle").split("_")[0]
  const zoomedSceneKey = zoomedAgent
    ? getSceneKey(
        zoomedActivityType,
        zoomedAgent.homeCity || zoomedPlaceKey || "default"
      )
    : null
  const zoomedScene = zoomedSceneKey ? locationScenes[zoomedSceneKey] : null
  const zoomedSceneGenerating = zoomedScene?.status === "generating"

  useEffect(() => {
    const supabase = createClient()
    let active = true

    const syncAccessToken = async () => {
      const { data, error } = await supabase.auth.getSession()
      if (!active) return

      if (error) {
        setAccessToken(null)
        return
      }

      setAccessToken(data?.session?.access_token ?? null)
    }

    void syncAccessToken()

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      if (!active) return
      setAccessToken(session?.access_token ?? null)
    })

    return () => {
      active = false
      subscription.unsubscribe()
    }
  }, [setAccessToken])

  useEffect(() => {
    if (!WORLD_DEBUG_ENABLED || typeof window === "undefined") return
    setGuestBypassEnabled(
      window.sessionStorage.getItem(DEV_GUEST_BYPASS_KEY) === "1"
    )
  }, [])

  useEffect(() => {
    setWorldEntryErrorMessage("")
    setWorldEntryAccessState(isWorldEntryRoute ? "checking" : "ready")
  }, [isWorldEntryRoute, pathname])

  // Bootstrap agents
  useEffect(() => {
    let cancelled = false
    let retryTimer = null
    let coldToastActive = false

    const resolveUserId = async () => {
      try {
        const supabase = createClient()
        const { data } = await supabase.auth.getSession()
        return data?.session?.user?.id ?? null
      } catch {
        return null
      }
    }

    const loadBootstrap = async () => {
      const userId = await resolveUserId()

      // --- warm path: cache hit ---
      // Render immediately from the cached stable tier (agents, hats,
      // world_state) and kick off a quiet background revalidate. The follow-up
      // fetch must still apply the full bootstrap payload so task/activity
      // state lands even when the agent roster itself is unchanged.
      if (isWorldEntryRoute && !guestBypassEnabled && userId) {
        const cached = readCachedBootstrap(userId)
        if (cached?.bootstrap?.openclaw_connected) {
          applyBootstrap(cached.bootstrap, { stableOnly: true })
          setWorldEntryErrorMessage("")
          setBootstrapRetryCount(0)
          setWorldEntryAccessState("ready")

          const revalidate = beginRevalidateToast()
          try {
            const fresh = await fetchWorldBootstrap()
            if (cancelled) return
            if (!fresh?.openclaw_connected && needsOpenClawOnboarding(fresh?.user)) {
              clearCachedBootstrap()
              setWorldEntryAccessState("onboarding")
              revalidate.error("Couldn't reach OpenClaw — try reconnecting")
              return
            }
            const cachedSig = getBootstrapAgentIdSignature(cached.bootstrap)
            const freshSig = getBootstrapAgentIdSignature(fresh)
            applyResolvedBootstrap(fresh, { userId, preserveRuntime: true })
            if (cachedSig !== freshSig) {
              revalidate.successChanged()
            } else {
              revalidate.successUnchanged()
            }
          } catch (err) {
            if (cancelled) return
            revalidate.error(err instanceof Error ? err.message : undefined)
          }
          return
        }
      }

      // --- cold path ---
      try {
        if (isWorldEntryRoute && guestBypassEnabled) {
          if (useWorldStore.getState().agents.length === 0) {
            seedAgents()
          }
          dismissColdBootstrapToast()
          setWorldEntryErrorMessage("")
          setBootstrapRetryCount(0)
          setWorldEntryAccessState("ready")
          return
        }

        if (isWorldEntryRoute) {
          if (!coldToastActive) {
            beginColdBootstrapToast()
            coldToastActive = true
          } else {
            updateColdBootstrapToast("checking")
          }
          const gateState = await fetchAuthGateState()

          if (!cancelled && gateState.mode === "login") {
            dismissColdBootstrapToast()
            setWorldEntryErrorMessage("")
            setBootstrapRetryCount(0)
            setWorldEntryAccessState("login")
            return
          }

          if (!cancelled && gateState.mode === "onboarding") {
            dismissColdBootstrapToast()
            setWorldEntryErrorMessage("")
            setBootstrapRetryCount(0)
            setWorldEntryAccessState("onboarding")
            return
          }
        }

        if (isWorldEntryRoute) {
          updateColdBootstrapToast("import")
        }
        const importPromise = isWorldEntryRoute
          ? importWorldAgents()
          : Promise.resolve(null)
        let importStillRunning = false

        try {
          const importResult = await Promise.race([
            importPromise.then((response) => {
              if (
                response &&
                typeof response === "object" &&
                Array.isArray(response.failed) &&
                response.failed.length > 0
              ) {
                // Backend couldn't sync every agent — surface it so the user
                // knows the UI may be rendering stale/empty character data.
                console.warn(
                  "[world] /import returned partial failures:",
                  response.failed
                )
              }
              return "completed"
            }),
            new Promise((resolve) => {
              window.setTimeout(
                () => resolve("timeout"),
                WORLD_IMPORT_INITIAL_WAIT_MS
              )
            }),
          ])

          importStillRunning = importResult === "timeout"
          if (importStillRunning && isWorldEntryRoute) {
            updateColdBootstrapToast("importSlow")
          }
        } catch (importError) {
          if (isTransientBootstrapError(importError)) {
            throw importError
          }
          console.warn("[world] /import failed:", importError)
        }

        if (isWorldEntryRoute && !importStillRunning) {
          updateColdBootstrapToast("bootstrap")
        }
        const bootstrap = await fetchWorldBootstrap()
        if (!cancelled) {
          applyResolvedBootstrap(bootstrap, { userId })
          if (isWorldEntryRoute) {
            finishColdBootstrapToast()
          }
        }

        // Always re-fetch bootstrap after the full import settles. Character
        // config generation runs inside /world/import and can commit to Mongo
        // right before (or during) the initial bootstrap fetch above, so a
        // second fetch guarantees the panel reflects the newly generated
        // personality/backstory without requiring a manual refresh.
        if (isWorldEntryRoute) {
          void importPromise
            .then(async () => {
              const refreshedBootstrap = await fetchWorldBootstrap()
              if (cancelled) return
              applyResolvedBootstrap(refreshedBootstrap, {
                userId,
                preserveRuntime: true,
              })
            })
            .catch((refreshError) => {
              console.warn(
                "[world] post-import bootstrap refresh failed:",
                refreshError
              )
            })
        }
      } catch (err) {
        if (!cancelled) {
          const transientBootstrapError = isTransientBootstrapError(err)

          if (
            transientBootstrapError &&
            isWorldEntryRoute &&
            bootstrapRetryCount < BOOTSTRAP_MAX_RETRIES
          ) {
            updateColdBootstrapToast("authRetry")
            // Shared identity can resolve slightly before the client-side
            // Supabase session hydrates. Keep retrying bootstrap for signed-in
            // world users instead of failing immediately.
            retryTimer = window.setTimeout(() => {
              setBootstrapRetryCount((current) => current + 1)
            }, BOOTSTRAP_RETRY_DELAY_MS)
            return
          }

          if (transientBootstrapError && isWorldEntryRoute) {
            dismissColdBootstrapToast()
            setWorldEntryErrorMessage("")
            setBootstrapRetryCount(0)
            setWorldEntryAccessState("login")
            return
          }

          setBootstrapRetryCount(0)
          setWorldEntryErrorMessage(
            err instanceof Error ? err.message : "World bootstrap failed."
          )
          setWorldEntryAccessState(isWorldEntryRoute ? "error" : "ready")
          if (isWorldEntryRoute) {
            errorColdBootstrapToast(
              err instanceof Error ? err.message : undefined
            )
          }
        }
      }
    }

    loadBootstrap()
    return () => {
      cancelled = true
      if (retryTimer !== null) {
        window.clearTimeout(retryTimer)
      }
    }
  }, [
    applyBootstrap,
    applyResolvedBootstrap,
    bootstrapRetryCount,
    guestBypassEnabled,
    isWorldEntryRoute,
    seedAgents,
  ])

  useEffect(() => {
    if (!shouldRunWorldLiveSync || worldEntryAccessState !== "ready") {
      return
    }

    let cancelled = false
    let reconnectTimer = null
    const presenceDebounceRef = { current: null }
    let streamAbortController = new AbortController()

    const scheduleReconnect = () => {
      if (cancelled) return
      reconnectTimer = window.setTimeout(() => {
        reconnectTimer = null
        if (!cancelled) {
          streamAbortController = new AbortController()
          void connect()
        }
      }, 1000)
    }

    const connect = async () => {
      try {
        await streamWorldTaskEvents({
          signal: streamAbortController.signal,
          onEvent: (event) => {
            if (cancelled || !event?.agent_id) {
              return
            }
            applyAgentTaskSnapshot(event.agent_id, event)
          },
          onPresence: (event) => {
            if (cancelled) return
            setOpenclawConnected(event.connected)
            if (event.connected) {
              if (presenceDebounceRef.current !== null) {
                window.clearTimeout(presenceDebounceRef.current)
              }
              presenceDebounceRef.current = window.setTimeout(() => {
                presenceDebounceRef.current = null
                if (cancelled) return
                clearCachedBootstrap()
                void fetchWorldBootstrap()
                  .then((bootstrap) => {
                    if (!cancelled) {
                      applyResolvedBootstrap(bootstrap, {
                        preserveRuntime: true,
                      })
                    }
                  })
                  .catch(() => {})
              }, 500)
            } else {
              setWorldEntryErrorMessage("")
              setBootstrapRetryCount(0)
              if (isWorldEntryRoute) {
                setWorldEntryAccessState("ready")
              }
            }
          },
        })
        if (!cancelled && !streamAbortController.signal.aborted) {
          scheduleReconnect()
        }
      } catch {
        if (!cancelled && !streamAbortController.signal.aborted) {
          scheduleReconnect()
        }
      }
    }

    void connect()

    return () => {
      cancelled = true
      streamAbortController.abort()
      if (reconnectTimer !== null) {
        window.clearTimeout(reconnectTimer)
      }
      if (presenceDebounceRef.current !== null) {
        window.clearTimeout(presenceDebounceRef.current)
      }
    }
  }, [
    applyAgentTaskSnapshot,
    applyResolvedBootstrap,
    guestBypassEnabled,
    isWorldEntryRoute,
    setOpenclawConnected,
    shouldRunWorldLiveSync,
    worldEntryAccessState,
  ])

  useEffect(() => {
    if (
      !shouldRunWorldLiveSync ||
      worldEntryAccessState !== "ready" ||
      guestBypassEnabled
    ) {
      return
    }

    let cancelled = false
    let refreshing = false

    const refreshBootstrap = async ({ force = false } = {}) => {
      if (refreshing || cancelled) return
      if (
        typeof document !== "undefined" &&
        document.visibilityState === "hidden"
      ) {
        return
      }
      refreshing = true
      try {
        let userId = null
        try {
          const supabase = createClient()
          const { data } = await supabase.auth.getSession()
          userId = data?.session?.user?.id ?? null
        } catch {
          userId = null
        }

        // Freshness cooldown: alt-tab / focus events skip the refetch if the
        // cached bootstrap is still within TTL. Explicit signals (install,
        // chat-app task changes) pass {force: true} to bypass.
        if (!force && userId && readCachedBootstrap(userId)) {
          return
        }

        const revalidate = beginRevalidateToast()
        try {
          const previousSignature = getStoreAgentIdSignature(
            useWorldStore.getState().agents
          )
          const bootstrap = await fetchWorldBootstrap()
          if (cancelled) return

          const applied = applyResolvedBootstrap(bootstrap, {
            userId,
            preserveRuntime: true,
          })
          if (!applied) {
            revalidate.error("Couldn't reach OpenClaw — try reconnecting")
            return
          }

          const nextSignature = getBootstrapAgentIdSignature(bootstrap)
          if (previousSignature !== nextSignature) {
            revalidate.successChanged()
          } else {
            revalidate.successUnchanged()
          }
        } catch (error) {
          if (!cancelled) {
            revalidate.error(error instanceof Error ? error.message : undefined)
          }
        }
      } finally {
        refreshing = false
      }
    }

    const handleFocus = () => {
      void refreshBootstrap()
    }
    const handleWorldTasksChanged = (event) => {
      const detail = event?.detail
      if (
        detail?.agentId &&
        (detail.tasks !== undefined ||
          detail.world !== undefined ||
          detail.life_sim !== undefined)
      ) {
        applyAgentTaskSnapshot(
          detail.agentId,
          {
            tasks: detail.tasks,
            world: detail.world,
            life_sim: detail.life_sim,
          }
        )
        return
      }
      if (guestBypassEnabled) {
        return
      }
      // Chat-app signalled a structural change (agent roster, skills, etc.);
      // bypass the cache cooldown.
      void refreshBootstrap({ force: true })
    }
    const handleVisibilityChange = () => {
      if (guestBypassEnabled) {
        return
      }
      if (document.visibilityState === "visible") {
        void refreshBootstrap()
      }
    }

    window.addEventListener(
      CHAT_APP_WORLD_TASKS_CHANGED_EVENT,
      handleWorldTasksChanged
    )
    if (!guestBypassEnabled) {
      window.addEventListener("focus", handleFocus)
      document.addEventListener("visibilitychange", handleVisibilityChange)
    }

    return () => {
      cancelled = true
      if (!guestBypassEnabled) {
        window.removeEventListener("focus", handleFocus)
        document.removeEventListener("visibilitychange", handleVisibilityChange)
      }
      window.removeEventListener(
        CHAT_APP_WORLD_TASKS_CHANGED_EVENT,
        handleWorldTasksChanged
      )
    }
  }, [
    applyAgentTaskSnapshot,
    applyResolvedBootstrap,
    guestBypassEnabled,
    shouldRunWorldLiveSync,
    worldEntryAccessState,
  ])

  const enableGuestBypass = () => {
    if (!WORLD_DEBUG_ENABLED || typeof window === "undefined") return
    window.sessionStorage.setItem(DEV_GUEST_BYPASS_KEY, "1")
    seedAgents()
    setGuestBypassEnabled(true)
    setWorldEntryAccessState("ready")
  }

  const disableGuestBypass = () => {
    if (!WORLD_DEBUG_ENABLED || typeof window === "undefined") return
    window.sessionStorage.removeItem(DEV_GUEST_BYPASS_KEY)
    useWorldStore.setState({
      agents: [],
      selectedAgentId: null,
      hoveredAgentId: null,
      draggedAgentId: null,
      mapZoomTarget: null,
      lifeSimState: {},
      activeTasks: {},
      taskQueues: {},
      completedTasks: [],
      activityLog: [],
      taskLog: [],
      activeToolUse: {},
      accessToken: null,
      openResultModals: [],
      activeCalls: [],
      activeChatEmbeds: [],
      floatingWindowOrder: [],
      activityFeedVisible: false,
      taskManagerOpen: false,
      showCreator: false,
      agentTravel: {},
    })
    setGuestBypassEnabled(false)
    setWorldEntryAccessState("checking")
  }

  // M key to toggle views
  useEffect(() => {
    const handler = (e) => {
      if (e.target.tagName === "INPUT" || e.target.tagName === "TEXTAREA")
        return
      if (e.key === "m" || e.key === "M") {
        const current = useWorldStore.getState().activeView
        useWorldStore
          .getState()
          .setActiveView(current === "agent" ? "world" : "agent")
      }
    }
    window.addEventListener("keydown", handler)
    return () => window.removeEventListener("keydown", handler)
  }, [])

  // ff-test server check
  useEffect(() => {
    if (!WORLD_DEBUG_ENABLED) return
    fetch("/api/backgrounds")
      .then((r) => {
        if (!r.ok) setServerBanner(true)
      })
      .catch(() => setServerBanner(true))
  }, [])

  if (
    isWorldEntryRoute &&
    (worldEntryAccessState === "onboarding" ||
      worldEntryAccessState === "login" ||
      worldEntryAccessState === "error")
  ) {
    const debugPanel = (
      <DevGuestBypassPanel
        enabled={guestBypassEnabled}
        onEnable={enableGuestBypass}
        onDisable={disableGuestBypass}
      />
    )
    const shellContent =
      worldEntryAccessState === "onboarding" ? (
        <OnboardingScreen withNavbar={false} />
      ) : worldEntryAccessState === "login" ? (
        <LoginScreen
          withNavbar={false}
          next={pathname}
          authError={authError}
          authReason={authReason}
        />
      ) : (
        <RootShellFailureState message={worldEntryErrorMessage} />
      )

    return (
      <>
        <TopBar showDashboardControls={false} />
        {debugPanel}
        <div style={{ position: "relative", zIndex: 1 }}>{shellContent}</div>
        <GlobalToastMount />
      </>
    )
  }

  return (
    <>
      <TopBar
        showDashboardControls={
          !isWorldEntryRoute || worldEntryAccessState === "ready"
        }
      />
      {isWorldEntryRoute && guestBypassEnabled && (
        <DevGuestBypassPanel
          enabled={guestBypassEnabled}
          onEnable={enableGuestBypass}
          onDisable={disableGuestBypass}
        />
      )}
      {(!isWorldEntryRoute || worldEntryAccessState === "ready") && (
        <ActivityFeed />
      )}
      {(!isWorldEntryRoute || worldEntryAccessState === "ready") && (
        <TaskManager />
      )}

      {/* 3D world always rendered, behind page content */}
      <div style={{ position: "fixed", inset: 0, zIndex: 0 }}>
        <EgoWorldClient />
      </div>

      {/* Page content renders on top */}
      <div style={{ position: "relative", zIndex: 1 }}>
        {!isWorldEntryRoute || worldEntryAccessState === "ready"
          ? children
          : null}
      </div>

      {WORLD_DEBUG_ENABLED && activeView === "world" && (
        <div
          style={{
            position: "fixed",
            top: 84,
            right: 12,
            zIndex: 95,
            width: 220,
            borderRadius: 8,
            background: "rgba(8,10,16,0.82)",
            backdropFilter: "blur(20px)",
            border: "1px solid rgba(255,255,255,0.12)",
            padding: 10,
            display: "flex",
            flexDirection: "column",
            gap: 8,
            boxShadow: "inset 0px 1px 1px rgba(255,255,255,0.06)",
          }}
        >
          <div
            style={{
              fontFamily: "'Inter', ui-sans-serif, system-ui, sans-serif",
              fontSize: 10,
              letterSpacing: "0.12em",
              textTransform: "uppercase",
              color: "rgba(255,255,255,0.45)",
            }}
          >
            Debug
          </div>

          <button
            onClick={() => setSceneDebugGeometry(!sceneDebugGeometry)}
            style={{
              height: 30,
              borderRadius: 8,
              background: sceneDebugGeometry
                ? "rgba(0,255,200,0.18)"
                : "rgba(255,255,255,0.05)",
              border: `1px solid ${sceneDebugGeometry ? "rgba(0,255,200,0.65)" : "rgba(255,255,255,0.16)"}`,
              padding: "0 12px",
              fontFamily: "'Inter', ui-sans-serif, system-ui, sans-serif",
              fontSize: 10,
              fontWeight: 500,
              color: sceneDebugGeometry ? "#00ffc8" : "rgba(255,255,255,0.72)",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              transition: "all 0.15s",
            }}
          >
            {sceneDebugGeometry ? "Hide Shell Debug" : "Show Shell Debug"}
          </button>

          <button
            onClick={() => {
              if (zoomedSceneKey) {
                regenerateScene(zoomedActivityType, zoomedSceneKey)
              }
            }}
            disabled={!zoomedSceneKey || zoomedSceneGenerating}
            style={{
              height: 30,
              borderRadius: 8,
              background:
                zoomedSceneKey && !zoomedSceneGenerating
                  ? "rgba(8,10,16,0.82)"
                  : "rgba(255,255,255,0.03)",
              border: `1px solid ${zoomedSceneKey && !zoomedSceneGenerating ? "rgba(0,255,200,0.3)" : "rgba(255,255,255,0.08)"}`,
              padding: "0 12px",
              fontFamily: "'Inter', ui-sans-serif, system-ui, sans-serif",
              fontSize: 10,
              fontWeight: 500,
              color:
                zoomedSceneKey && !zoomedSceneGenerating
                  ? "rgba(0,255,200,0.82)"
                  : "rgba(255,255,255,0.28)",
              cursor:
                zoomedSceneKey && !zoomedSceneGenerating
                  ? "pointer"
                  : "default",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            ↻ Clear + regen BG
          </button>

          <div
            style={{
              fontFamily: "'Inter', ui-sans-serif, system-ui, sans-serif",
              fontSize: 9,
              lineHeight: 1.4,
              color: zoomedSceneKey
                ? "rgba(255,255,255,0.48)"
                : "rgba(255,255,255,0.26)",
              minHeight: 24,
            }}
          >
            {zoomedSceneKey
              ? zoomedSceneGenerating
                ? `Scene: ${zoomedSceneKey} (generating)`
                : `Scene: ${zoomedSceneKey}`
              : "Open a zoomed location scene to target its background."}
          </div>
        </div>
      )}

      {(!isWorldEntryRoute || worldEntryAccessState === "ready") && (
        <AgentPanel />
      )}
      {(!isWorldEntryRoute || worldEntryAccessState === "ready") && (
        <ChatWidget />
      )}
      {(!isWorldEntryRoute || worldEntryAccessState === "ready") && (
        <MasterChat />
      )}
      {(!isWorldEntryRoute || worldEntryAccessState === "ready") && (
        <TaskToasts />
      )}
      <GlobalToastMount />
      {(!isWorldEntryRoute || worldEntryAccessState === "ready") &&
        openResultModals.map((task) => (
          <TaskResultModal
            key={getTaskIdentity(task)}
            task={task}
            onClose={() => closeResultModal(task)}
          />
        ))}
      {(!isWorldEntryRoute || worldEntryAccessState === "ready") && (
        <ChatEmbedWindow />
      )}
      {(!isWorldEntryRoute || worldEntryAccessState === "ready") && (
        <DroppedAgentTaskComposer />
      )}
      {(!isWorldEntryRoute || worldEntryAccessState === "ready") && (
        <CallWindow />
      )}
      {(!isWorldEntryRoute || worldEntryAccessState === "ready") &&
        showCreator && <AgentCreator onClose={() => setShowCreator(false)} />}

      {WORLD_DEBUG_ENABLED && serverBanner && (
        <div
          style={{
            position: "fixed",
            bottom: 70,
            left: "50%",
            transform: "translateX(-50%)",
            background: "rgba(8,10,16,0.95)",
            border: "1px solid rgba(255,255,255,0.1)",
            borderRadius: 8,
            padding: "8px 16px",
            display: "flex",
            alignItems: "center",
            gap: 10,
            zIndex: 200,
            fontFamily: "'Inter', ui-sans-serif, system-ui, sans-serif",
            fontSize: 10,
            color: "rgba(255,255,255,0.5)",
          }}
        >
          <span style={{ color: "#fbbf24" }}>⚠</span>
          Scene generation offline — start the ff-test server to enable location
          scenes
          <button
            onClick={() => setServerBanner(false)}
            style={{
              background: "none",
              border: "none",
              color: "rgba(255,255,255,0.4)",
              fontSize: 14,
              cursor: "pointer",
              fontFamily: "'Inter', ui-sans-serif, system-ui, sans-serif",
            }}
          >
            ×
          </button>
        </div>
      )}
    </>
  )
}

function PersistentShellInner({ children }) {
  const pathname = usePathname()
  const isAuthPage = AUTH_PATHS.some((p) => pathname?.startsWith(p))

  useEffect(() => {
    if (typeof window === "undefined") return
    if (process.env.NODE_ENV === "production" && !window.navigator?.webdriver) {
      return
    }

    window.__egoWorldStore = useWorldStore

    return () => {
      if (window.__egoWorldStore === useWorldStore) {
        delete window.__egoWorldStore
      }
    }
  }, [])

  if (isAuthPage) return <>{children}</>

  return (
    <PersistentShellContent pathname={pathname}>
      {children}
    </PersistentShellContent>
  )
}

function GlobalToastMount() {
  const toasts = useToastStore((s) => s.toasts)
  const dismissToast = useToastStore((s) => s.dismissToast)
  return <ToastContainer toasts={toasts} onDismiss={dismissToast} />
}

export default function PersistentShell({ children }) {
  const mounted = useSyncExternalStore(
    () => () => {},
    () => true,
    () => false
  )

  if (!mounted) return <>{children}</>

  return <PersistentShellInner>{children}</PersistentShellInner>
}
